import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable()
export class ApiService {
  constructor(private httpClient: HttpClient) {}

  objectToQueryString(obj: any): string {
    const keyValuePairs = [];
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value !== null && value !== undefined) {
          keyValuePairs.push(
            encodeURIComponent(key) + '=' + encodeURIComponent(value),
          );
        }
      }
    }
    return keyValuePairs.join('&');
  }

  private dataUrlToBlob(dataUrl: string): Blob {
    const [meta, base64] = String(dataUrl || '').split(',');
    const mimeMatch = meta?.match(/data:(.*?);base64/);
    const mimeType = mimeMatch?.[1] || 'application/octet-stream';
    const binary = atob(base64 || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }

  private normalizeUploadInput(data: any = {}): FormData | any {
    const candidate = data?.file ?? data;

    if (candidate instanceof File || candidate instanceof Blob) {
      const formData = new FormData();
      const fileName =
        candidate instanceof File ? candidate.name : `upload-${Date.now()}.bin`;
      formData.append('file', candidate, fileName);
      return formData;
    }

    if (typeof candidate === 'string' && candidate.startsWith('data:')) {
      const blob = this.dataUrlToBlob(candidate);
      const ext = blob.type?.split('/')[1] || 'bin';
      const formData = new FormData();
      formData.append('file', blob, `upload-${Date.now()}.${ext}`);
      return formData;
    }

    return data;
  }

  // AUTH
  loginAdmin = (data): Observable<any> =>
    this.httpClient.post('auth/admin/login', data);
  registerAdmin = (data): Observable<any> =>
    this.httpClient.post('create/admin', data);

  // REVIEWS
  getReviewById = (id): Observable<any> => this.httpClient.get(`ratings/${id}`);
  getRatingsByEntities = (entity): Observable<any> =>
    this.httpClient.get(`ratings-entity/${entity}`);
  deleteRatings = (id: string): Observable<any> =>
    this.httpClient.delete(`rating/${id}`);

  // UPLOAD
  fileUpload(data, type = 'form'): Observable<any> {
    const payload = this.normalizeUploadInput(data);
    return this.httpClient.post('file', payload);
  }

  uploadImage = (data): Observable<any> =>
    this.httpClient.post('file-upload', this.normalizeUploadInput(data));

  // NFTs
  getNftCollections = (): Observable<any> =>
    this.httpClient.get('all-nft-collections');
  newNftCollection = (data): Observable<any> =>
    this.httpClient.post('new-nft-collection', data);
  newNft = (data): Observable<any> => this.httpClient.post('new-nft', data);
  getSingleCollection = (id): Observable<any> =>
    this.httpClient.get('nft-collection/' + id);
  nftCollectionStatusUpdate = (id): Observable<any> =>
    this.httpClient.get('nft-collection-update/' + id);
  nftStatusUpdate = (id): Observable<any> =>
    this.httpClient.get('nft-update/' + id);

  // BANNERS
  addBanner = (data): Observable<any> =>
    this.httpClient.post(`admin/banners`, data);
  getBanners = (): Observable<any> => this.httpClient.get('admin/banners');
  deleteBanner = (id): Observable<any> =>
    this.httpClient.delete('admin/banners/' + id);

  // CATEGORIES
  getEntityCategories = (): Observable<any> =>
    this.httpClient.get('categories?sort=ASC');
  getRatingCategories = (id: string): Observable<any> =>
    this.httpClient.post(`get-rating-category`, { category: id });
  addSuperCategory = (data): Observable<any> =>
    this.httpClient.post('admin/add-supercategory/', data);
  getAllSuperCategory = (): Observable<any> =>
    this.httpClient.get('all-supercategories');
  updateSuperCategory = (data): Observable<any> =>
    this.httpClient.patch('admin/update-supercategory/' + data.id, data);
  deleteSuperCategory = (id): Observable<any> =>
    this.httpClient.delete('admin/delete-supercategory/' + id);

  // ENTITIES
  getAllEntities = (data): Observable<any> =>
    this.httpClient.post(`entities`, data);
  getEntitiesByCategory = (id): Observable<any> =>
    this.httpClient.get(`entities/${id}`);
  getRelatedEntities = (id): Observable<any> =>
    this.httpClient.get(`entities/${id}/related`);
  getChildEntities = (id): Observable<any> =>
    this.httpClient.get(`entities/${id}/child`);

  // Miscellaneous APIs
  deleteInformativeParameter = (id): Observable<any> =>
    this.httpClient.delete(`informative-parameter/${id}`);
  deleteSubjectiveParameter = (id): Observable<any> =>
    this.httpClient.delete(`sub-parameter/${id}`);

  // Profile Claim
  claimProfileRequests(query: any = {}, data: any = {}): Observable<any> {
    let queryObj = query;
    if (typeof query === 'number' || typeof query === 'string') {
      queryObj = { page: query };
    }
    const queryString = this.objectToQueryString(queryObj || {});
    return this.httpClient.post(
      'admin/claim-profile-requests' + (queryString ? `?${queryString}` : ''),
      data || {},
    );
  }
  claimProfileAction = (data): Observable<any> =>
    this.httpClient.post(`admin/claim-profile-action`, data);
  createProfile = (data): Observable<any> =>
    this.httpClient.post(`admin/create-profile`, data);

  // MISC
  getGlobalIcons = (): Observable<any> => this.httpClient.get(`global/icons`);
  updateGlobalIcons = (data): Observable<any> =>
    this.httpClient.put(`global/icons`, data);
  addFamousQuote = (data): Observable<any> =>
    this.httpClient.post(`add-famous-quote`, data);

  getAllFamousQuote(data = {}, query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.post(
      'get-all-famous-quote' + (queryString ? `?${queryString}` : ''),
      data,
    );
  }

  deleteFamousQuote = (id): Observable<any> =>
    this.httpClient.delete(`delete-famous-quote/${id}`);
  quotesStatusUpdate = (data): Observable<any> =>
    this.httpClient.put(`quotes-status-update/${data.id}`, data);

  // USERS
  getAllUsers = (params): Observable<any> =>
    this.httpClient.post(`all-users`, params);
  getAdminFundingRecords(query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/users/user-funding' + (queryString ? `?${queryString}` : ''),
    );
  }
  getFundingAttachmentAccessUrl = (key: string): Observable<any> =>
    this.httpClient.get(
      `user/user-funding/attachment/access?key=${encodeURIComponent(key || '')}`,
    );
  updateAdminFundingRecordVerification = (recordId, data): Observable<any> =>
    this.httpClient.patch(
      `admin/users/user-funding/${recordId}/verification`,
      data,
    );

  // SURVEY
  addPoll = (data): Observable<any> =>
    this.httpClient.post(`admin/surveys`, data);

  getPolls(query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/surveys' + (queryString ? `?${queryString}` : ''),
    );
  }

  getPoll = (id): Observable<any> => this.httpClient.get('admin/surveys/' + id);
  updatePoll = (data): Observable<any> =>
    this.httpClient.patch('admin/surveys/' + data.surveyId, data);
  updatePollStatus = (data): Observable<any> =>
    this.httpClient.put('admin/surveys/' + data.surveyId, data);
  announcePollResult = (data): Observable<any> =>
    this.httpClient.put('admin/surveys/result/' + data.surveyId, data);

  // Podcasts
  getPodcasts(query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/podcasts' + (queryString ? `?${queryString}` : ''),
    );
  }

  createPodcast = (data): Observable<any> =>
    this.httpClient.post('admin/podcasts', data);
  getYoutubePodcastMetadata = (url: string): Observable<any> =>
    this.httpClient.get(
      `admin/podcasts/youtube-metadata?url=${encodeURIComponent(url)}`,
    );
  updatePodcast = (data): Observable<any> =>
    this.httpClient.patch('admin/podcasts/' + data.id, data);
  deletePodcast = (id): Observable<any> =>
    this.httpClient.delete('admin/podcasts/' + id);

  uploadMediaV2 = (file): Observable<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post('v2/media', formData);
  };

  // Marketplace books
  getMarketplaceBooks = (): Observable<any> =>
    this.httpClient.get('admin/marketplace/books');
  createMarketplaceBook = (data): Observable<any> =>
    this.httpClient.post('admin/marketplace/books', data);
  updateMarketplaceBook = (id, data): Observable<any> =>
    this.httpClient.patch('admin/marketplace/books/' + id, data);
  deleteMarketplaceBook = (id): Observable<any> =>
    this.httpClient.delete('admin/marketplace/books/' + id);

  // Twitter Golden List
  getTwitterGoldenList(query: any = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/twitter-golden-list' + (queryString ? `?${queryString}` : ''),
    );
  }
  createTwitterGoldenListMember = (data): Observable<any> =>
    this.httpClient.post('admin/twitter-golden-list', data);
  importTwitterGoldenList = (file: File): Observable<any> => {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.httpClient.post('admin/twitter-golden-list/import', formData);
  };
  collectTwitterGoldenListPosts = (): Observable<any> =>
    this.httpClient.post('admin/twitter-golden-list/posts/collect', {});
  retryFailedTwitterGoldenListPosts = (): Observable<any> =>
    this.httpClient.post('admin/twitter-golden-list/posts/retry-failed', {});
  getLatestTwitterGoldenListPostRun = (): Observable<any> =>
    this.httpClient.get('admin/twitter-golden-list/posts/runs/latest');
  getTwitterWebhookRequests(query: any = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/twitter-golden-list/webhook-requests' +
        (queryString ? `?${queryString}` : ''),
    );
  }
  deleteTwitterWebhookRequest = (id): Observable<any> =>
    this.httpClient.delete(`admin/twitter-golden-list/webhook-requests/${id}`);
  getTwitterPostingSession = (): Observable<any> =>
    this.httpClient.get('admin/twitter-session');
  loginTwitterPostingSession = (data): Observable<any> =>
    this.httpClient.post('admin/twitter-session/login', data);
  getTwitterOauthStatus = (): Observable<any> =>
    this.httpClient.get('admin/twitter-session/oauth-status');
  setTwitterOauthRefreshToken = (data): Observable<any> =>
    this.httpClient.post('admin/twitter-session/oauth-refresh-token', data);
  getTweetPostLogs(query: any = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/twitter-session/posts' + (queryString ? `?${queryString}` : ''),
    );
  }
  getTwitterGoldenListMemberPosts(id, query: any = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      `admin/twitter-golden-list/${id}/posts` +
        (queryString ? `?${queryString}` : ''),
    );
  }
  updateTwitterGoldenListMember = (id, data): Observable<any> =>
    this.httpClient.patch('admin/twitter-golden-list/' + id, data);
  deleteTwitterGoldenListMember = (id): Observable<any> =>
    this.httpClient.delete('admin/twitter-golden-list/' + id);

  // E-commerce
  getEcommerceCategories = (): Observable<any> =>
    this.httpClient.get('admin/ecommerce/categories');
  createEcommerceCategory = (data): Observable<any> =>
    this.httpClient.post('admin/ecommerce/categories', data);
  updateEcommerceCategory = (id, data): Observable<any> =>
    this.httpClient.patch(`admin/ecommerce/categories/${id}`, data);
  updateEcommerceCategoryActivation = (id, isActive: boolean): Observable<any> =>
    this.httpClient.patch(`admin/ecommerce/categories/${id}/activation`, {isActive});
  deleteEcommerceCategory = (id): Observable<any> =>
    this.httpClient.delete(`admin/ecommerce/categories/${id}`);
  getEcommerceProducts = (): Observable<any> =>
    this.httpClient.get('admin/ecommerce/products');
  createEcommerceProduct = (data): Observable<any> =>
    this.httpClient.post('admin/ecommerce/products', data);
  updateEcommerceProduct = (id, data): Observable<any> =>
    this.httpClient.patch(`admin/ecommerce/products/${id}`, data);
  deleteEcommerceProduct = (id): Observable<any> =>
    this.httpClient.delete(`admin/ecommerce/products/${id}`);
  uploadEcommerceAsset(file, kind = 'image'): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`admin/ecommerce/upload?kind=${kind}`, formData);
  }
  previewEcommerceDownload = (key): Observable<any> =>
    this.httpClient.post('admin/ecommerce/download-preview', {key});
  getEcommerceOrders = (query = {}): Observable<any> => {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get('admin/ecommerce/orders' + (queryString ? `?${queryString}` : ''));
  };
  getEcommerceOrder = (id): Observable<any> =>
    this.httpClient.get(`admin/ecommerce/orders/${id}`);
  deleteEcommerceOrder = (id): Observable<any> =>
    this.httpClient.delete(`admin/ecommerce/orders/${id}`);
  updateEcommerceOrderStatus = (id, data): Observable<any> =>
    this.httpClient.patch(`admin/ecommerce/orders/${id}/status`, data);
  retryEcommerceOrderDelivery = (id): Observable<any> =>
    this.httpClient.post(`admin/ecommerce/orders/${id}/retry-delivery`, {});

  // Marketplace projects
  getMarketplaceProjects(query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/marketplace/projects' + (queryString ? `?${queryString}` : ''),
    );
  }

  createMarketplaceProject = (data): Observable<any> =>
    this.httpClient.post('admin/marketplace/projects', data);

  updateMarketplaceProject = (id, data): Observable<any> =>
    this.httpClient.patch('admin/marketplace/projects/' + id, data);
  deleteMarketplaceProject = (id): Observable<any> =>
    this.httpClient.delete('admin/marketplace/projects/' + id);

  // Marketplace satire
  getMarketplaceSatires(query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/marketplace/satires' + (queryString ? `?${queryString}` : ''),
    );
  }

  createMarketplaceSatire = (data): Observable<any> =>
    this.httpClient.post('admin/marketplace/satires', data);

  updateMarketplaceSatire = (id, data): Observable<any> =>
    this.httpClient.patch('admin/marketplace/satires/' + id, data);
  deleteMarketplaceSatire = (id): Observable<any> =>
    this.httpClient.delete('admin/marketplace/satires/' + id);

  createMarketplaceProjectRole = (projectId, data): Observable<any> =>
    this.httpClient.post(`admin/marketplace/projects/${projectId}/roles`, data);

  updateMarketplaceProjectRole = (projectId, roleId, data): Observable<any> =>
    this.httpClient.patch(
      `admin/marketplace/projects/${projectId}/roles/${roleId}`,
      data,
    );

  createMarketplaceSatireRole = (projectId, data): Observable<any> =>
    this.httpClient.post(`admin/marketplace/satires/${projectId}/roles`, data);

  updateMarketplaceSatireRole = (projectId, roleId, data): Observable<any> =>
    this.httpClient.patch(
      `admin/marketplace/satires/${projectId}/roles/${roleId}`,
      data,
    );

  getMarketplaceProjectApplications(query = {}): Observable<any> {
    const queryString = this.objectToQueryString(query);
    return this.httpClient.get(
      'admin/marketplace/applications' + (queryString ? `?${queryString}` : ''),
    );
  }

  updateMarketplaceProjectApplication = (id, data): Observable<any> =>
    this.httpClient.patch(`admin/marketplace/applications/${id}`, data);

  addUserMarketplaceProjectMembership = (data): Observable<any> =>
    this.httpClient.post('admin/marketplace/project-memberships', data);

  getStaticMembershipData = (): Observable<any> =>
    this.httpClient.get('admin/static/memberships/constants');
  getAllMemberships = (): Observable<any> =>
    this.httpClient.get('admin/memberships');
  getSingleMembership = (id): Observable<any> =>
    this.httpClient.get('admin/memberships/' + id);
  createMembership = (data): Observable<any> =>
    this.httpClient.post('admin/memberships', data);
  assignMembership = (id, data): Observable<any> =>
    this.httpClient.put(`admin/users/${id}/memberships`, data);
  updateMembership = (id, data): Observable<any> =>
    this.httpClient.patch('admin/memberships/' + id, data);

  // Organization
  getAllOrganizations = (params = {}): Observable<any> =>
    this.httpClient.get('admin/users/organizations', { params });
  getSingleOrganization = (id): Observable<any> =>
    this.httpClient.get('admin/users/organizations/' + id);
  createOrganization = (data): Observable<any> =>
    this.httpClient.post('admin/users/organizations', data);
  deleteOrganization = (id): Observable<any> =>
    this.httpClient.delete(`admin/users/organizations/` + id);
  updateOrganization = (id, data): Observable<any> =>
    this.httpClient.patch('admin/users/organizations/' + id, data);

  // Import data
  importUsers = (data): Observable<any> =>
    this.httpClient.post('admin/users/import', data);
  importEntities = (data): Observable<any> =>
    this.httpClient.post('admin/entities/import', data);
  importRatings = (data): Observable<any> =>
    this.httpClient.post('admin/entities/ratings/import', data);
  importEntityLookup = (data): Observable<any> =>
    this.httpClient.post('admin/entities/lookup/import', data);
  importDemographicClockCities = (data): Observable<any> =>
    this.httpClient.post('admin/demographic-clock/city/import', data);

  // Organization
  getAllHindutvaLaboratory = (params = {}): Observable<any> =>
    this.httpClient.get('admin/hindutva-laboratory', { params });
  createHindutvaLaboratory = (data): Observable<any> =>
    this.httpClient.post('admin/hindutva-laboratory/create', data);
  deleteHindutvaLaboratory = (id): Observable<any> =>
    this.httpClient.delete(`admin/hindutva-laboratory/` + id);
  updateHindutvaLaboratory = (id, data): Observable<any> =>
    this.httpClient.patch('admin/hindutva-laboratory/update/' + id, data);

  // Score models
  getAllScoreModels = (params = {}): Observable<any> =>
    this.httpClient.get('admin/bhakts/score_models', { params });
}
